'use client';

import { c as _c } from "react/compiler-runtime";
import { Alert, Slide, Snackbar, type SlideProps, type SnackbarCloseReason } from '@mui/material';
import { useState, type FC, type PropsWithChildren, type SyntheticEvent } from 'react';
import { ToastContext, type AddOrReplaceToast, type ToastContextValue, type ToastItem, type ToastMethods } from './ToastContext';
const SlideTransition: FC<SlideProps> = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Slide {...props} direction="left" />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
const AUTO_HIDE_DURATION = 4000;
const TOAST_GAP = 8;
const TOAST_HEIGHT = 48;
export const ToastProvider: FC<PropsWithChildren> = ({
  children
}) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const addOrReplaceToast: AddOrReplaceToast = (callerId, message, severity, autoHide) => {
    setToasts(prev => {
      const existingIndex = prev.findIndex(t => t.callerId === callerId && t.open);
      const newId = Math.random().toString(36);
      if (existingIndex >= 0) {
        // Replace existing toast from same caller, reset timer by updating id
        const updated = prev.map((t_0, i) => i === existingIndex ? {
          ...t_0,
          id: newId,
          message,
          severity,
          autoHide
        } : t_0);
        return updated;
      }

      // Add new toast
      const newToasts = [...prev, {
        id: newId,
        callerId,
        message,
        severity,
        open: true,
        autoHide
      }];
      return newToasts;
    });
  };
  const closeToast = (id: ToastItem['id']) => {
    setToasts(prev_0 => prev_0.map(t_1 => t_1.id === id ? {
      ...t_1,
      open: false
    } : t_1));
  };

  // #TODO dalmo - this causes infinite rendering.... is this even useful?
  // useToastFromQueryParam(addOrReplaceToast);

  const removeToast = (id_0: ToastItem['id']) => {
    setToasts(prev_1 => prev_1.filter(t_2 => t_2.id !== id_0));
  };
  const handleClose = (id_1: string) => (_event: SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') return;
    closeToast(id_1);
  };
  const handleExited = (id_2: ToastItem['id']) => () => {
    removeToast(id_2);
  };
  const createToastMethods = (callerId_0: string): ToastMethods => ({
    success: (message_0: string) => addOrReplaceToast(callerId_0, message_0, 'success', true),
    error: (message_1: string) => addOrReplaceToast(callerId_0, message_1, 'error', false),
    warning: (message_2: string) => addOrReplaceToast(callerId_0, message_2, 'warning', true),
    info: (message_3: string) => addOrReplaceToast(callerId_0, message_3, 'info', true)
  });
  const contextValue: ToastContextValue = (callerId_1 = Math.random().toString(36)) => {
    return createToastMethods(callerId_1);
  };
  const openToasts = toasts.filter(t_3 => t_3.open);
  return <ToastContext value={contextValue}>
      {children}
      {toasts.map(toast => {
      const openIndex = openToasts.findIndex(t_4 => t_4.id === toast.id);
      const bottomOffset = openIndex >= 0 ? (openToasts.length - 1 - openIndex) * (TOAST_HEIGHT + TOAST_GAP) + 24 : 24;
      const autoHideDuration = toast.autoHide ? AUTO_HIDE_DURATION : null;
      const testId = `toast-${toast.callerId}-${toast.severity}`;
      return <Snackbar key={toast.id} open={toast.open} autoHideDuration={autoHideDuration} onClose={handleClose(toast.id)} anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }} slots={{
        transition: SlideTransition
      }} slotProps={{
        transition: {
          onExited: handleExited(toast.id)
        }
      }} sx={{
        bottom: `${bottomOffset}px !important`,
        transition: 'bottom 300ms ease-in-out'
      }}>
            <Alert onClose={handleClose(toast.id)} severity={toast.severity} variant="filled" sx={{
          width: '100%',
          minWidth: 288
        }} data-testid={testId}>
              {toast.message}
            </Alert>
          </Snackbar>;
    })}
    </ToastContext>;
};